import { Component, Input, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { DepartmentService } from '@ftr/api-core'
import { NotFoundApiError } from '@ftr/api-shared'
import { CourtSystemUser } from '@ftr/contracts/api/court-system'
import { Department } from '@ftr/contracts/api/department'
import { EntityType } from '@ftr/contracts/api/permissions'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, PageTitleService, mapData, tapData, tapFailure } from '@ftr/foundation'
import { StaticMenuItem } from '@ftr/menus'
import { AppPaths, CourtSystemAdminPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { CoreConfigurationService, CourtSystemService } from '@ftr/ui-court-system'
import { LocationService, RootLocation } from '@ftr/ui-location'
import { Store } from '@ngxs/store'
import { BehaviorSubject, Observable, combineLatest, map, shareReplay, switchMap } from 'rxjs'
import { UserState } from '../../store'

export interface UserProfileAdminViewModel {
  user: CourtSystemUser
  enabledRestrictions: EntityType[]
  rootLocations: RootLocation[]
  departments: Department[]
}
@Component({
  selector: 'ftr-user-profile-admin-view',
  templateUrl: './user-profile-admin-view.component.html',
  styleUrls: ['./user-profile-admin-view.component.css'],
})
export class UserProfileAdminViewComponent implements OnInit {
  @Input() courtSystemId: Observable<Uuid>
  @Input() userId: Observable<Uuid>
  @Input() userGroupId?: Observable<Uuid>

  navItems$: Observable<StaticMenuItem[]>
  viewModel: ApiResult<UserProfileAdminViewModel>

  private readonly refreshSubject = new BehaviorSubject(undefined)

  constructor(
    private readonly router: Router,
    private readonly courtSystemService: CourtSystemService,
    private readonly pageTitleService: PageTitleService,
    private readonly coreConfigurationService: CoreConfigurationService,
    private readonly departmentService: DepartmentService,
    private readonly locationService: LocationService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.navItems$ = this.buildNavItems()

    this.viewModel = combineLatest([this.courtSystemId, this.userId, this.refreshSubject]).pipe(
      switchMap(([courtSystemId, userId]) =>
        ApiResult.combine([
          this.courtSystemService.getUser(courtSystemId, userId),
          this.coreConfigurationService.getEnabledRestrictedPermissionEntities(courtSystemId),
          this.locationService.getAllRootsForCourtSystem(courtSystemId),
          this.departmentService
            .list(courtSystemId, false, 1)
            .pipe(mapData(pagedDepartments => pagedDepartments.items)),
        ]).pipe(
          tapFailure(err => {
            /**
             * A Not Found error occurs when a user is removed from their last group.
             */
            if (err.originalErrors[0] instanceof NotFoundApiError) {
              this.router.navigate([
                AppPaths.CourtSystem,
                courtSystemId,
                CourtSystemPaths.Admin,
                CourtSystemAdminPaths.UserGroups,
              ])
            }
          }),
        ),
      ),
      mapData(
        ([user, enabledRestrictions, rootLocations, departments]) =>
          ({ user, enabledRestrictions, rootLocations, departments }) as UserProfileAdminViewModel,
      ),
      tapData(({ user }) =>
        this.pageTitleService.setTitle(
          user.givenName ? `${user.givenName} ${user.familyName!.charAt(0)}.` : user.email,
        ),
      ),
      shareReplay(1),
    )
  }

  triggerRefresh(): void {
    this.refreshSubject.next(undefined)
  }

  private buildNavItems(): Observable<StaticMenuItem[]> {
    return combineLatest({
      courtSystemId: this.courtSystemId,
      fnHasPermission: this.store.select(UserState.hasAnyPermissionInCourtSystem),
    }).pipe(
      map(latest => {
        const navItems = [
          {
            title: 'Edit User',
            path: ['edit'],
          },
          {
            title: 'Remove User',
            path: ['remove'],
          },
        ]
        if (latest.fnHasPermission([UserGroupPermissionId.AccessAuditLogs], latest.courtSystemId)) {
          navItems.push({
            title: 'Audit Log',
            path: ['audit-log'],
          })
        }
        return navItems
      }),
    )
  }
}
